/*
 * pathenc — the subject-token ⇄ symbol-path codec, ported byte-for-byte from the
 * symbol-indexer's Go `internal/pathenc` (and the rules in
 * nats-fabric-wire-contract.md). LIBRARY-GRADE: no DOM, no framework.
 *
 * WHY THE FRONTEND NEEDS THIS. Two backend streams identify the same symbol two
 * different ways:
 *   • SymbolIndex.Subscribe → SymbolRow.path is DECODED ("DB50.Buffer193.Slot[3]")
 *     (the indexer calls DecodeSymbolPath when it ingests the catalog).
 *   • Values.Subscribe → ValueEvent.symbolId is the ENCODED subject sid
 *     ("DB50_Buffer193_Slot_x5b3_x5d") (api-gateway strips it straight off the
 *     live_values KV key).
 * The grid keys symbols by decoded path, so we DECODE each value's sid to bind it
 * to its catalog row. Encoding rules (decoded → token): '.'→'_', '_'→'__',
 * other non-[A-Za-z0-9] → '_xHH' (upper-hex byte). PLC paths are ASCII, so the
 * byte-oriented Go logic maps cleanly onto charCodeAt.
 */

function isAlnum(c: number): boolean {
  return (c >= 0x30 && c <= 0x39) || (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a);
}
// Case-insensitive: encoders emit uppercase canonically, but some plugins
// (mock-core-plugin) emit lowercase — decoders accept either so '[' (5b) / ']'
// (5d) and other a–f bytes round-trip.
function isHex(c: number): boolean {
  return (c >= 0x30 && c <= 0x39) || (c >= 0x41 && c <= 0x46) || (c >= 0x61 && c <= 0x66);
}
function unhex(c: number): number {
  if (c <= 0x39) return c - 0x30; // '0'-'9'
  if (c <= 0x46) return c - 0x37; // 'A'-'F'
  return c - 0x57; // 'a'-'f'
}

/** Encode a decoded symbol path into its NATS subject token. */
export function encodeSymbolPath(path: string): string {
  let out = '';
  for (let i = 0; i < path.length; i++) {
    const c = path.charCodeAt(i);
    if (c === 0x2e) out += '_'; // '.'
    else if (c === 0x5f) out += '__'; // '_'
    else if (isAlnum(c)) out += path[i];
    else out += '_x' + c.toString(16).toUpperCase().padStart(2, '0');
  }
  return out;
}

/**
 * Decode a NATS subject token back to its symbol path. Lenient: unexpected bytes
 * pass through (the Go version errors, but for value-binding we never want a bad
 * token to throw — a malformed sid just yields a best-effort key that won't match
 * a catalog row, which is the correct silent no-op).
 */
export function decodeSymbolPath(token: string): string {
  let out = '';
  let i = 0;
  while (i < token.length) {
    const c = token.charCodeAt(i);
    if (c !== 0x5f) {
      // not '_'
      out += token[i];
      i++;
      continue;
    }
    // c === '_': look ahead.
    if (i + 1 < token.length && token.charCodeAt(i + 1) === 0x5f) {
      out += '_';
      i += 2;
      continue;
    }
    if (
      i + 3 < token.length &&
      token[i + 1] === 'x' &&
      isHex(token.charCodeAt(i + 2)) &&
      isHex(token.charCodeAt(i + 3))
    ) {
      out += String.fromCharCode((unhex(token.charCodeAt(i + 2)) << 4) | unhex(token.charCodeAt(i + 3)));
      i += 4;
      continue;
    }
    // lone '_' → '.'
    out += '.';
    i++;
  }
  return out;
}
